import { useState, useEffect, useCallback } from 'react';

interface ClickDetector {
  hasClicked: boolean;
  resetClickStatus: () => void;
}

/**
 * Custom hook to detect if a click has occurred anywhere on the screen
 * since the component mounted or the status was reset.
 * * @returns {ClickDetector} An object with the current click status and a reset function.
 */
export const useScreenClickDetector = (): ClickDetector => {
  const [hasClicked, setHasClicked] = useState<boolean>(false);

  const resetClickStatus = useCallback(() => {
    setHasClicked(false);
  }, []);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      setHasClicked(true);
    };

    window.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('click', handleClick);
    };
  }, []); // Empty dependency array ensures listeners are added/removed only on mount/unmount

  return { hasClicked, resetClickStatus };
};
